import { CustomPagination } from 'components/CustomPagination';
import { DataTable } from 'components/DataTable/DataTable';
import type { CatalogOtherCostVariableItemDataSortInput } from 'gql/graphql';
import { cnMixSpace } from 'uikit';
import type { TableColumn, TableRow, Filters, SelectedFilters, onSortBy } from 'uikit';

import cn from 'classnames';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';

import Styles from './catalogTable.module.css';

type TTableProps<TData extends TableRow = any> = {
    catalogOtherCostVariablesData: TData[];
    tableColumns: TableColumn<TData>[];
    getAdditionalClassName?: (vars: any) => string;
    currentPage: number;
    currentPageSize: number;
    totalPages: number;
    setCurrentPage: any;
    setCurrentPageSize: any;
    filters?: Filters<TData>;
    setQueryOrder?: Dispatch<SetStateAction<CatalogOtherCostVariableItemDataSortInput[] | undefined>>;
    setQueryWhere?: (value: SetStateAction<SelectedFilters>) => void;
};

export const CatalogTable = <TData extends TableRow = any>(tableProps: TTableProps<TData>) => {
    const {
        catalogOtherCostVariablesData,
        tableColumns,
        getAdditionalClassName,
        filters,
        setQueryOrder,
        currentPage,
        currentPageSize,
        totalPages,
        setCurrentPage,
        setCurrentPageSize,
        setQueryWhere,
    } = tableProps;

    const getAdditionalClassNameCommon = useCallback(({ column }: { column: (typeof tableColumns)[number] }) => {
        if ('accessor' in column && column.accessor === 'name') {
            return 'nameColumn';
        }

        return '';
    }, []);

    const getClassName = useMemo(
        () => getAdditionalClassName ?? getAdditionalClassNameCommon,
        [getAdditionalClassName, getAdditionalClassNameCommon],
    );

    const handleSortChange = useCallback<onSortBy<TData>>(
        (config) => {
            setQueryOrder?.(config ? [{ [config.sortingBy]: config.sortOrder.toUpperCase() }] : undefined);
        },
        [setQueryOrder],
    );

    const handleCurrentPageSizeChange = useCallback(
        (newValue: number) => {
            setCurrentPage(0);
            setCurrentPageSize(newValue);
        },
        [setCurrentPage, setCurrentPageSize],
    );

    const handleFiltersUpdated = useCallback(
        ({ name, groupName, subsidiaryName }: SelectedFilters) => {
            setQueryWhere?.((prevQueryWhere) => ({ ...prevQueryWhere, name, groupName, subsidiaryName }));
            setCurrentPage(0);
        },
        [setCurrentPage, setQueryWhere],
    );

    return (
        <div
            className={cn(
                Styles.table,
                cnMixSpace({
                    mT: 'l',
                    mH: 'xl',
                }),
            )}
        >
            <DataTable
                columns={tableColumns}
                filters={filters}
                data={catalogOtherCostVariablesData}
                onFiltersUpdated={handleFiltersUpdated}
                onSortBy={handleSortChange}
                borderBetweenRows
                borderBetweenColumns={false}
                getAdditionalClassName={getClassName}
                verticalAlign="center"
            />
            <CustomPagination
                position="space-between"
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
                currentPageSize={currentPageSize}
                setCurrentPageSize={handleCurrentPageSizeChange}
            />
        </div>
    );
};

CatalogTable.displayName = 'CatalogTable';
